import { useState, useMemo, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import { API } from "../../../http-common";
import { ROLE_OPTIONS, BUSINESS, getBusinessName, ROLE } from "../../../helpers/constants";

export const AdminCreateUserModal = ({
  isOpen,
  toggle,
  onUserCreated,
  usersForRelation,
  editingUser,
}) => {
  const isEdit = !!editingUser;

  const [form, setForm] = useState({
    username: "",
    email: "",
    relation_user_id: "",
    initials: "",
    role_id: ROLE.EQ_COMERCIAL,
  });

  const [showAllRelations, setShowAllRelations] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [crossEnabled, setCrossEnabled] = useState(false);
  const [crossLoading, setCrossLoading] = useState(false);
  const [crossTouched, setCrossTouched] = useState(false);
  const [adminEnabled, setAdminEnabled] = useState(false);
  const [adminTouched, setAdminTouched] = useState(false);
  const [adviserSearchText, setAdviserSearchText] = useState("");
  const [selectedAdviserIds, setSelectedAdviserIds] = useState([]);

  const roleOptions = ROLE_OPTIONS;

  const showDirectorField = form.role_id === ROLE.EQ_COMERCIAL || form.role_id === ROLE.EQ_COMERCIAL_ASISTENTE || form.role_id === ROLE.EQ_COMERCIAL_VIEWER || form.role_id === ROLE.EQ_TEAM_LEADER;
  const showAdviserVisibilityField = form.role_id === ROLE.EQ_TEAM_LEADER && !!form.relation_user_id;

  const directorOptions = useMemo(() => {
  const all = (usersForRelation || []).filter(
    (u) => u.role_id === ROLE.EQ_COMERCIAL_LIDER
  );

  if (showAllRelations) return all;
  return all.filter(
    (u) => u.relation_user_id === 0 || u.relation_user_id == null
  );
  }, [showAllRelations, usersForRelation]);

  const adviserOptions = useMemo(() => {
    if (!showAdviserVisibilityField) return [];
    const dirId = Number(form.relation_user_id);
    const text = adviserSearchText.trim().toLowerCase();

    return (usersForRelation || []).filter((u) => {
      const isSelf = isEdit && u.id === editingUser?.id;
      const isAdviserUnderDirector =
        u.role_id === ROLE.EQ_COMERCIAL && Number(u.relation_user_id) === dirId;
      const notAlreadySelected = !selectedAdviserIds.includes(u.id);
      const matchesSearch =
        !text ||
        (u.username || "").toLowerCase().includes(text) ||
        (u.email || "").toLowerCase().includes(text);
      return !isSelf && isAdviserUnderDirector && notAlreadySelected && matchesSearch;
    });
  }, [showAdviserVisibilityField, form.relation_user_id, adviserSearchText, selectedAdviserIds, usersForRelation, isEdit, editingUser]);

  const selectedAdvisers = useMemo(
    () => (usersForRelation || []).filter((u) => selectedAdviserIds.includes(u.id)),
    [usersForRelation, selectedAdviserIds]
  );

  const editingUserId = editingUser?.id ?? null;
  const biz = editingUser?.business_id ?? null;

  const showBusinessSwitch = isEdit && (biz === BUSINESS.IFA || biz === BUSINESS.CANALES);
  const otherBizId = biz === BUSINESS.IFA ? BUSINESS.CANALES : biz === BUSINESS.CANALES ? BUSINESS.IFA : null;
  const currentBizName = getBusinessName(biz);
  const otherBizName = getBusinessName(otherBizId);
  
  useEffect(() => {
    if (!isOpen) return;

    if (isEdit && editingUser) {
      setForm({
        username: editingUser.username || "",
        email: editingUser.email || "",
        relation_user_id:
          editingUser.relation_user_id && editingUser.relation_user_id !== 0
            ? String(editingUser.relation_user_id)
            : "",
        initials: editingUser.initials || "",
        role_id: editingUser.role_id ?? ROLE.EQ_COMERCIAL,
      });
      setAdminEnabled(!!editingUser.isAdmin);
        if (editingUser.role_id === ROLE.EQ_TEAM_LEADER) {
          API.get(`/user/${editingUser.id}/visible-advisers`)
            .then((resp) => setSelectedAdviserIds(resp?.data?.visible_adviser_ids || []))
            .catch(() => setSelectedAdviserIds([]));
        } else {
          setSelectedAdviserIds([]);
        }
    } else {
      setForm({
        username: "",
        email: "",
        relation_user_id: "",
        initials: "",
        role_id: ROLE.EQ_COMERCIAL,
      });
      setCrossEnabled(false);
      setAdminEnabled(false);
      setSelectedAdviserIds([]);
    }
    setCrossTouched(false);
    setErrorMsg("");
    setShowAllRelations(false);
    setAdminTouched(false);
    setAdviserSearchText("");
  }, [isOpen, isEdit, editingUser]);


  useEffect(() => {
    let alive = true;

    const loadCrossAccess = async () => {
      if (!isOpen) return;

      if (!isEdit || !editingUserId || !showBusinessSwitch || otherBizId == null) {
        if (alive) setCrossEnabled(false);
        return;
      }

      if (alive) setCrossLoading(true);

      try {
        const resp = await API.get(`/user/${editingUserId}/business-access`);
        const allowable = Array.isArray(resp?.data?.allowable_business_ids)
          ? resp.data.allowable_business_ids
          : [];

        const next = allowable.includes(otherBizId);

        if (alive) setCrossEnabled(next);
      } catch (e) {
        if (alive) setCrossEnabled(false);
      } finally {
        if (alive) setCrossLoading(false);
      }
    };

    loadCrossAccess();

    return () => {
      alive = false;
    };
  }, [isOpen, isEdit, editingUserId, showBusinessSwitch, otherBizId]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: name === "role_id" ? Number(value) : value,
    }));
  };

  const handleAddAdviser = (adviserId) => {
    setSelectedAdviserIds((prev) => [...prev, adviserId]);
    setAdviserSearchText("");
  };

  const handleRemoveAdviser = (adviserId) => {
    setSelectedAdviserIds((prev) => prev.filter((id) => id !== adviserId));
  };

  const handleSubmit = async () => {
  setLoading(true);
  setErrorMsg("");

  if(showDirectorField && !form.relation_user_id) {
    setErrorMsg("Debe seleccionar un director para este rol.");
    setLoading(false);
    return
  }  

  try {
    const basePayload = {
      username: form.username,
      email: form.email,
      initials: form.initials,
      relation_user_id:
        showDirectorField && form.relation_user_id
          ? Number(form.relation_user_id)
          : 0,
      role_id: Number(form.role_id),
    };

    let response;

    if (isEdit && editingUser?.id) {
      response = await API.put(`/user/${editingUser.id}`, basePayload);

      if (showBusinessSwitch && otherBizId != null && crossTouched) {
        await API.patch(`/user/${editingUser.id}/business-access`, {
          business_id: otherBizId,
          enabled: crossEnabled,
        });
      }
      if (adminTouched) {
        await API.patch(`/user/${editingUser.id}/admin-access`, {
          enabled: adminEnabled,
        });
      }
    } else {
      response = await API.post("/user", basePayload);
    }
      const success =
      response.status === 200 ||
      response.status === 201 ||
      response.data?.ok === true ||
      response.data?.error === 0 ||
      !!response.data?.id;

    if (success) {
      if (form.role_id === ROLE.EQ_TEAM_LEADER) {
        const teamLeaderId = isEdit ? editingUser.id : response.data?.id;
        if (teamLeaderId) {
          await API.patch(`/user/${teamLeaderId}/visible-advisers`, {
            adviser_ids: selectedAdviserIds,
          });
        }
      }
      onUserCreated();
      toggle();
      return;
    }

    setErrorMsg("Error inesperado al guardar el usuario.");
  } catch (error) {
    console.error("Error creando/actualizando usuario:", error);
    setErrorMsg(
      error?.response?.data?.errors?.[0]?.msg ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Error al guardar usuario."
    );
  } finally {
    setLoading(false);
  }
};

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>
        {isEdit ? "Editar usuario" : "Crear nuevo usuario"}
      </ModalHeader>

      <ModalBody>
        <Form>
          <FormGroup style={{ marginBottom: 20 }}>
            <Label>Nombre completo</Label>
            <Input
              type="text"
              name="username"
              value={form.username}
              onChange={handleChange}
              placeholder="Nombre Apellido"
            />
          </FormGroup>

          <FormGroup style={{ marginBottom: 20 }}>
            <Label>Email del usuario</Label>
            <Input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="usuario@balanz.com"
              disabled={isEdit}
            />
          </FormGroup>

          <FormGroup style={{ marginBottom: 20 }}>
            <Label>Iniciales</Label>
            <Input
              type="text"
              name="initials"
              value={form.initials}
              onChange={handleChange}
              placeholder="Indique las iniciales (ej: AA)"
            />
          </FormGroup>

          <FormGroup style={{ marginBottom: 20 }}>
            <Label>Rol</Label>
            <Input
              type="select"
              name="role_id"
              value={form.role_id}
              onChange={handleChange}
            >
              {roleOptions.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </Input>
          </FormGroup>

          {showDirectorField && (
            <FormGroup style={{ marginBottom: 20 }}>
              <Label>Mail del director</Label>

              <Input
                type="select"
                name="relation_user_id"
                value={form.relation_user_id}
                onChange={handleChange}
                style={{ marginBottom: 10 }}
              >
                <option value="">Seleccione un director</option>
                {directorOptions.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.username} ({u.email})
                  </option>
                ))}
              </Input>
            </FormGroup>
          )}

          {showAdviserVisibilityField && (
            <FormGroup style={{ marginBottom: 20 }}>
              <Label>Visibilidad sobre asesores</Label>

              <div className="d-flex flex-wrap gap-2" style={{ marginBottom: 8 }}>
                {selectedAdvisers.map((adviser) => (
                  <span
                    key={adviser.id}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      background: "#e9ecef",
                      borderRadius: 16,
                      padding: "4px 10px",
                      fontSize: 13,
                    }}
                  >
                    {adviser.username}
                    <button
                      type="button"
                      onClick={() => handleRemoveAdviser(adviser.id)}
                      style={{
                        border: "none",
                        background: "transparent",
                        marginLeft: 6,
                        cursor: "pointer",
                        lineHeight: 1,
                      }}
                      aria-label={`Quitar ${adviser.username}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>

              <Input
                type="text"
                placeholder="Buscar asesor por nombre o email..."
                value={adviserSearchText}
                onChange={(e) => setAdviserSearchText(e.target.value)}
              />

              {adviserSearchText.trim() && (
                <div
                  style={{
                    border: "1px solid #ced4da",
                    borderRadius: 4,
                    marginTop: 4,
                    maxHeight: 160,
                    overflowY: "auto",
                  }}
                >
                  {adviserOptions.length === 0 ? (
                    <div style={{ padding: 8, fontSize: 13, color: "#6c757d" }}>
                      Sin resultados.
                    </div>
                  ) : (
                    adviserOptions.map((adviser) => (
                      <div
                        key={adviser.id}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleAddAdviser(adviser.id)}
                        style={{ padding: "6px 10px", cursor: "pointer", fontSize: 14 }}
                      >
                        {adviser.username} ({adviser.email})
                      </div>
                    ))
                  )}
                </div>
              )}
            </FormGroup>
          )}

          {showBusinessSwitch && (
            <FormGroup style={{ marginTop: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Input
                  type="checkbox"
                  id="crossBusiness"
                  checked={crossEnabled}
                  onChange={(e) => {
                    setCrossEnabled(e.target.checked);
                    setCrossTouched(true);
                  }}
                />
                <Label check htmlFor="crossBusiness" style={{ margin: 0 }}>
                  Este usuario está en la unidad de negocio{" "}
                  <b>{currentBizName}</b>. ¿Permitir cambiarlo a{" "}
                  <b>{otherBizName}</b>?
                </Label>
              </div>
            </FormGroup>
          )}
          {isEdit && (
          <FormGroup style={{ marginTop: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Input
                type="checkbox"
                id="adminAccess"
                checked={adminEnabled}
                onChange={(e) => {
                  setAdminEnabled(e.target.checked);
                  setAdminTouched(true);
                }}
              />
              <Label check htmlFor="adminAccess" style={{ margin: 0 }}>
                Permitir acceso al ABM de usuarios (administrador)
              </Label>
            </div>
          </FormGroup>
        )}
          {errorMsg && (
            <p style={{ color: "red", marginTop: 10, fontSize: 14 }}>
              {errorMsg}
            </p>
          )}
        </Form>
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Cancelar
        </Button>

        <Button
          color="primary"
          style={{ background: "#24589c" }}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? "Guardando..." : isEdit ? "Guardar cambios" : "Confirmar"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

